/**
 * Knowledge item vocabulary (spec 20 §4): the values of the database enums
 * `knowledge_category`, `knowledge_status` and `knowledge_origin`, shared by
 * the API, the worker's context builders (module 80) and tests.
 *
 * `KNOWLEDGE_CATEGORIES` is the whole enum in database order: the twelve
 * tourism values of migration 0010, then the generic values of migration 0061
 * (S20-G2, ADR-023). Which of them a tenant may use on create/update is its
 * industry profile's list (`INDUSTRY_PROFILE_REGISTRY`); enum values are never
 * dropped, only not offered.
 */
export const KNOWLEDGE_CATEGORIES = [
    'house',
    'amenities',
    'wellness',
    'location',
    'summer',
    'winter',
    'cross_country',
    'summer_card',
    'faq',
    'host',
    'gastronomy',
    'excursions',
    'company',
    'services',
    'products',
    'team',
    'references',
    'offers',
    'events',
    'about_us',
];
/** The categories of the `tourism` profile: exactly the values every tenant had before ADR-023, in picker order. */
export const TOURISM_KNOWLEDGE_CATEGORIES = [
    'house',
    'amenities',
    'wellness',
    'location',
    'summer',
    'winter',
    'cross_country',
    'summer_card',
    'faq',
    'host',
    'gastronomy',
    'excursions',
    'about_us',
];
/** The categories of the `general` profile (ADR-023, S20-G2), in picker order. */
export const GENERAL_KNOWLEDGE_CATEGORIES = [
    'company',
    'services',
    'products',
    'team',
    'location',
    'references',
    'offers',
    'events',
    'faq',
    'about_us',
];
/** `verified` is reachable only through the human verification action (REQ-20-007). */
export const KNOWLEDGE_STATUSES = ['draft', 'verified', 'outdated'];
/** `system`: created through `KnowledgeDraftWriter` (AI job, director, promoted research). */
export const KNOWLEDGE_ORIGINS = ['human', 'system'];
/** Field limits of `knowledge_items`; API validation and the web editor use the same numbers. */
export const KNOWLEDGE_ITEM_LIMITS = {
    titleMaxLength: 200,
    bodyMaxLength: 10000,
    sourceNoteMaxLength: 500,
    sourceUrlMaxLength: 1000,
};
/** Field limits of `content_pillars` (spec 20 §4); API validation and the web editor use the same numbers. */
export const CONTENT_PILLAR_LIMITS = {
    nameMaxLength: 80,
    descriptionMaxLength: 1000,
    maxPosition: 32767,
    /** Upper bound of entries in one `PUT /v1/content-pillars/target-shares` body. */
    maxShares: 100,
};
/** Stable pillar key: lower-case letter, then 1-39 lower-case letters, digits or `_` (immutable). */
export const PILLAR_KEY_PATTERN = /^[a-z][a-z0-9_]{1,39}$/;
/** A target share as the API takes it: `numeric(5,2)` decimal string, e.g. `"12.5"`, `"12.50"`, `"100"`. */
export const TARGET_SHARE_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;
/** The whole mix of a property's active pillars, in hundredths of a percent (100.00 %). */
export const TARGET_SHARE_TOTAL_HUNDREDTHS = 10000;
/**
 * A target share decimal string in integer hundredths (`"12.5"` → 1250), so
 * sums are exact without ever passing through a float. `null` for anything
 * that is not a share between 0 and 100 with at most two decimals.
 */
export function shareToHundredths(value) {
    if (!TARGET_SHARE_PATTERN.test(value))
        return null;
    const [whole, fraction = ''] = value.split('.');
    const hundredths = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
    return hundredths <= TARGET_SHARE_TOTAL_HUNDREDTHS ? hundredths : null;
}
/** Integer hundredths back to the API's fixed two-decimal string (1250 → `"12.50"`). */
export function hundredthsToShare(hundredths) {
    const sign = hundredths < 0 ? '-' : '';
    const abs = Math.abs(hundredths);
    return `${sign}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}
/** Values of the database enum `quality_rule_kind` (spec 20 §4), in list order. */
export const QUALITY_RULE_KINDS = ['avoid', 'prefer'];
/** Limits of `quality_rules` (spec 20 §4); API validation and the web editor use the same numbers. */
export const QUALITY_RULE_LIMITS = {
    textMaxLength: 500,
    maxPosition: 32767,
    /** Rules per property, both kinds together: every rule goes into a prompt. */
    maxPerProperty: 50,
};
/** Field limits of the structured property address (spec 20 §4, REQ-20-027); API validation and the web form use the same numbers. */
export const PROPERTY_ADDRESS_LIMITS = {
    streetMaxLength: 120,
    postalCodeMaxLength: 20,
    cityMaxLength: 120,
};
/** Postal codes: letters, digits, space and hyphen only (spec 20 §4). */
export const PROPERTY_POSTAL_CODE_PATTERN = /^[A-Za-z0-9 -]+$/;
//# sourceMappingURL=knowledge.js.map